import { getHypercoreVaultRegistry } from './registry'
import type {
  HypercoreVault,
  HypercoreVaults,
  HypercoreVaultsFetchOptions,
} from './types'

const DEFAULT_INFO_URL = 'https://api.hyperliquid.xyz/info'

/** `vaultDetails` portfolio is `[[period, { accountValueHistory, … }], …]`. */
type PortfolioEntry = [string, { accountValueHistory?: [number, string][] }]

interface VaultDetailsResponse {
  name?: string
  leader?: string
  apr?: number
  isClosed?: boolean
  followers?: unknown[]
  portfolio?: PortfolioEntry[]
}

const postInfo = async <T>(url: string, body: unknown): Promise<T> => {
  const res = await fetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`Hyperliquid info ${res.status}`)
  return (await res.json()) as T
}

/**
 * The vault's current account value (TVL in USDC) = the last point of the
 * accountValueHistory. Prefer the `allTime` series, then `day`, then the
 * first available period.
 */
const latestAccountValue = (
  portfolio: PortfolioEntry[] | undefined,
): string | undefined => {
  if (!Array.isArray(portfolio) || portfolio.length === 0) return undefined
  const pick =
    portfolio.find((p) => p[0] === 'allTime') ??
    portfolio.find((p) => p[0] === 'day') ??
    portfolio[0]
  const hist = pick?.[1]?.accountValueHistory
  const last = hist?.[hist.length - 1]
  return last?.[1]
}

const fetchOne = async (
  apiUrl: string,
  entry: { address: string; name: string },
): Promise<HypercoreVault | null> => {
  try {
    const d = await postInfo<VaultDetailsResponse>(apiUrl, {
      type: 'vaultDetails',
      vaultAddress: entry.address,
    })
    const tvlUsd = latestAccountValue(d.portfolio)
    return {
      address: entry.address.toLowerCase(),
      name: d.name ?? entry.name,
      leader: d.leader?.toLowerCase(),
      tvlUsd: tvlUsd ?? '0',
      apr: typeof d.apr === 'number' ? d.apr : undefined,
      followerCount: Array.isArray(d.followers)
        ? d.followers.length
        : undefined,
      isClosed: typeof d.isClosed === 'boolean' ? d.isClosed : undefined,
      underlyingSymbol: 'USDC',
      yieldProfile: 'volatile',
      denomination: 'stable',
    }
  } catch {
    // A single vault's failure shouldn't sink the whole listing.
    return null
  }
}

/**
 * Fetch vault-level public data for the curated set of HyperCore vaults
 * via the Hyperliquid `vaultDetails` info API (read-only, no signing).
 * Returns a map keyed by lowercased address. Per-vault failures are
 * dropped rather than thrown.
 *
 * This is the listing counterpart to `getHypercoreUserPositions` — it
 * surfaces TVL / APR / leader for arbitrary HyperCore vaults, not just a
 * single user's positions.
 */
export const fetchHypercoreVaults = async (
  options?: HypercoreVaultsFetchOptions,
): Promise<HypercoreVaults> => {
  const apiUrl = options?.apiUrl ?? DEFAULT_INFO_URL
  const entries = options?.entries ?? getHypercoreVaultRegistry()
  const results = await Promise.all(entries.map((e) => fetchOne(apiUrl, e)))
  const out: HypercoreVaults = {}
  for (const v of results) if (v) out[v.address] = v
  return out
}
